import React from 'react';
import jsPDF from 'jspdf';

const PDFExportButton = ({ propertyData, calculatedMetrics, address, className = "", includeAdvanced = true }) => {
  
  const formatCurrency = (value) => {
    if (!value) return '$0';
    const num = parseFloat(value);
    if (isNaN(num)) return '$0';
    return num < 0 ? `-$${Math.abs(num).toLocaleString()}` : `$${num.toLocaleString()}`;
  };

  const formatPercentage = (value) => {
    if (!value) return '0%';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  // Calculate advanced metrics (same logic as AdvancedROIMetrics component)
  const calculateAdvancedMetrics = () => {
    const price = parseFloat(propertyData.propertyPrice) || 0;
    const monthlyRent = parseFloat(propertyData.monthlyRent) || 0;
    const downPaymentPercent = parseFloat(propertyData.downpaymentPercentage) / 100 || 1;
    const yearlyTax = parseFloat(propertyData.propertyTax) || 0;
    const yearlyInsurance = parseFloat(propertyData.yearlyInsurance) || 0;
    const annualHOA = (parseFloat(propertyData.monthlyHOA) || 0) * 12;
    const yearlyMaintenance = parseFloat(propertyData.yearlyMaintenance) || 0;
    const vacancyRate = parseFloat(propertyData.vacancyRate) / 100 || 0;
    const managementFee = parseFloat(propertyData.managementFeePercentage) / 100 || 0;
    const additionalExpenses = (parseFloat(propertyData.additionalExpenses) || 0) * 12;

    if (price === 0) return null;

    const annualRent = monthlyRent * 12;
    const effectiveRent = annualRent * (1 - vacancyRate);
    const totalOperatingExpenses = yearlyTax + yearlyInsurance + annualHOA + yearlyMaintenance + (effectiveRent * managementFee);
    const netOperatingIncome = effectiveRent - totalOperatingExpenses;
    const downPayment = price * downPaymentPercent;

    // Advanced Metrics
    const grossRentMultiplier = price / annualRent;
    const rentToPrice = (monthlyRent / price) * 100;
    const operatingExpenseRatio = (totalOperatingExpenses / effectiveRent) * 100;
    const debtServiceCoverageRatio = additionalExpenses > 0 ? netOperatingIncome / additionalExpenses : 'N/A';
    
    // Cash Flow Analysis
    const monthlyCashFlow = (netOperatingIncome - additionalExpenses) / 12;
    const annualCashFlow = netOperatingIncome - additionalExpenses;
    
    // Break-even Analysis
    const breakEvenOccupancy = ((totalOperatingExpenses + additionalExpenses) / annualRent) * 100;
    
    // Return Metrics
    const totalCashReturn = downPayment > 0 ? (annualCashFlow / downPayment) * 100 : 0;

    return {
      grossRentMultiplier: grossRentMultiplier.toFixed(2),
      rentToPrice: rentToPrice.toFixed(2),
      operatingExpenseRatio: operatingExpenseRatio.toFixed(1),
      debtServiceCoverageRatio: typeof debtServiceCoverageRatio === 'number' ? debtServiceCoverageRatio.toFixed(2) : debtServiceCoverageRatio,
      monthlyCashFlow: monthlyCashFlow.toFixed(0),
      annualCashFlow: annualCashFlow.toFixed(0),
      breakEvenOccupancy: breakEvenOccupancy.toFixed(1),
      totalCashReturn: totalCashReturn.toFixed(2),
      effectiveRent: effectiveRent.toFixed(0),
      totalOperatingExpenses: totalOperatingExpenses.toFixed(0)
    };
  };

  const generatePDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;
      let yPosition = 30;
      
      const advancedMetrics = includeAdvanced ? calculateAdvancedMetrics() : null;

      // Helper function to check if we need a new page
      const checkPageBreak = (currentY, neededSpace = 20) => {
        const pageHeight = doc.internal.pageSize.height;
        if (currentY + neededSpace > pageHeight - 30) { // 30 margin from bottom
          doc.addPage();
          return 30; // Reset to top margin
        }
        return currentY;
      };

      // Helper function to add text with automatic line wrapping
      const addText = (text, x, y, options = {}) => {
        // Check if we need a new page before adding text
        y = checkPageBreak(y, options.neededSpace || 20);
        
        doc.setFontSize(options.fontSize || 12);
        doc.setFont("helvetica", options.style || "normal");
        if (options.color) {
          doc.setTextColor(...options.color);
        } else {
          doc.setTextColor(0, 0, 0);
        }
        
        if (options.maxWidth) {
          const lines = doc.splitTextToSize(text, options.maxWidth);
          doc.text(lines, x, y);
          return y + (lines.length * (options.lineHeight || 7));
        } else {
          doc.text(text, x, y);
          return y + (options.lineHeight || 7);
        }
      };

      // Title
      yPosition = addText("Property ROI Analysis Report", margin, yPosition, {
        fontSize: 20,
        style: "bold",
        color: [59, 130, 246]
      });

      // Date
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      yPosition = addText(`Generated on ${currentDate}`, margin, yPosition + 5, {
        fontSize: 11,
        color: [107, 114, 128]
      });

      // Property Address
      if (address) {
        yPosition = addText(address, margin, yPosition + 15, {
          fontSize: 16,
          style: "bold",
          color: [59, 130, 246]
        });
      }

      // Key Metrics Section
      yPosition += 20;
      yPosition = checkPageBreak(yPosition, 60); // Need space for metrics section
      yPosition = addText("Key Investment Metrics", margin, yPosition, {
        fontSize: 14,
        style: "bold",
        neededSpace: 60
      });

      // Draw line under section title
      doc.setLineWidth(0.5);
      doc.setDrawColor(229, 231, 235);
      doc.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
      yPosition += 15;

      // Metrics in columns
      const col1X = margin;
      const col2X = margin + 60;
      const col3X = margin + 120;

      yPosition = addText("Cap Rate:", col1X, yPosition, { fontSize: 10, color: [107, 114, 128] });
      addText(formatPercentage(calculatedMetrics.capRate), col1X, yPosition, { fontSize: 14, style: "bold" });

      addText("Cash on Cash:", col2X, yPosition - 7, { fontSize: 10, color: [107, 114, 128] });
      addText(formatPercentage(calculatedMetrics.cashOnCash), col2X, yPosition, { fontSize: 14, style: "bold" });

      addText("Annual NOI:", col3X, yPosition - 7, { fontSize: 10, color: [107, 114, 128] });
      addText(formatCurrency(calculatedMetrics.netIncome), col3X, yPosition, { fontSize: 14, style: "bold" });

      // Property Details Section
      yPosition += 25;
      yPosition = checkPageBreak(yPosition, 100); // Need space for property details
      yPosition = addText("Property Details", margin, yPosition, {
        fontSize: 14,
        style: "bold",
        neededSpace: 100
      });

      // Draw line under section title
      doc.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
      yPosition += 15;

      // Property details in two columns
      const details = [
        ["Purchase Price:", formatCurrency(propertyData.propertyPrice)],
        ["Monthly Rent:", formatCurrency(propertyData.monthlyRent)],
        ["Annual Property Tax:", formatCurrency(propertyData.propertyTax)],
        ["Monthly HOA:", formatCurrency(propertyData.monthlyHOA)],
        ["Annual Insurance:", formatCurrency(propertyData.yearlyInsurance)],
        ["Management Fee:", formatPercentage(propertyData.managementFeePercentage)],
        ["Vacancy Rate:", formatPercentage(propertyData.vacancyRate)],
        ["Annual Maintenance:", formatCurrency(propertyData.yearlyMaintenance)],
        ["Down Payment:", formatPercentage(propertyData.downpaymentPercentage)],
        ["Additional Monthly Expenses:", formatCurrency(propertyData.additionalExpenses)]
      ];

      details.forEach((detail, index) => {
        const isLeftColumn = index % 2 === 0;
        const xPos = isLeftColumn ? margin : margin + 90;
        const yPos = yPosition + Math.floor(index / 2) * 15;

        addText(detail[0], xPos, yPos, { fontSize: 10, color: [107, 114, 128] });
        addText(detail[1], xPos, yPos + 7, { fontSize: 10, style: "bold" });
      });

      // Advanced Metrics Section (if enabled and available)
      if (advancedMetrics && includeAdvanced) {
        yPosition += 25;
        yPosition = checkPageBreak(yPosition, 120); // Need space for advanced metrics
        yPosition = addText("Advanced Investment Analytics", margin, yPosition, {
          fontSize: 14,
          style: "bold",
          neededSpace: 120
        });

        // Draw line under section title
        doc.line(margin, yPosition + 2, pageWidth - margin, yPosition + 2);
        yPosition += 15;

        // Property Ratios
        yPosition = addText("Property Ratios", margin, yPosition, {
          fontSize: 12,
          style: "bold",
          color: [59, 130, 246]
        });
        yPosition += 5;

        const ratios = [
          ["Gross Rent Multiplier:", advancedMetrics.grossRentMultiplier],
          ["Rent-to-Price Ratio:", `${advancedMetrics.rentToPrice}%`],
          ["Operating Expense Ratio:", `${advancedMetrics.operatingExpenseRatio}%`]
        ];

        ratios.forEach((ratio, index) => {
          const isLeftColumn = index % 2 === 0;
          const xPos = isLeftColumn ? margin : margin + 90;
          const yPos = yPosition + Math.floor(index / 2) * 10;

          addText(ratio[0], xPos, yPos, { fontSize: 9, color: [107, 114, 128] });
          addText(ratio[1], xPos + 50, yPos, { fontSize: 9, style: "bold" });
        });

        yPosition += 20;

        // Cash Flow Analysis
        yPosition = addText("Cash Flow Analysis", margin, yPosition, {
          fontSize: 12,
          style: "bold",
          color: [59, 130, 246]
        });
        yPosition += 5;

        const cashFlowMetrics = [
          ["Monthly Cash Flow:", formatCurrency(advancedMetrics.monthlyCashFlow)],
          ["Annual Cash Flow:", formatCurrency(advancedMetrics.annualCashFlow)],
          ["Cash-on-Cash Return:", `${advancedMetrics.totalCashReturn}%`],
          ["Break-even Occupancy:", `${advancedMetrics.breakEvenOccupancy}%`]
        ];

        cashFlowMetrics.forEach((metric, index) => {
          const isLeftColumn = index % 2 === 0;
          const xPos = isLeftColumn ? margin : margin + 90;
          const yPos = yPosition + Math.floor(index / 2) * 10;

          addText(metric[0], xPos, yPos, { fontSize: 9, color: [107, 114, 128] });
          addText(metric[1], xPos + 50, yPos, { fontSize: 9, style: "bold" });
        });

        yPosition += 30;
      }

      // Disclaimer - ensure it has enough space
      yPosition += 20;
      yPosition = checkPageBreak(yPosition, 80); // Need space for disclaimer
      yPosition = addText("DISCLAIMER", margin, yPosition, {
        fontSize: 12,
        style: "bold",
        color: [146, 64, 14],
        neededSpace: 80
      });

      const disclaimerText = "This analysis is for informational purposes only and should not be considered as professional financial, legal, or investment advice. Property investment involves risks, and past performance does not guarantee future results. Please consult with qualified professionals before making any investment decisions. The calculations are based on the data provided and certain assumptions that may not reflect actual market conditions or property performance.";
      
      yPosition = addText(disclaimerText, margin, yPosition + 5, {
        fontSize: 9,
        color: [146, 64, 14],
        maxWidth: pageWidth - (margin * 2),
        lineHeight: 5
      });

      // Footer - add to current page
      const currentPageHeight = doc.internal.pageSize.height;
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text("Generated by Property ROI Calculator • propertyroi.io", pageWidth / 2, currentPageHeight - 20, { align: 'center' });

      // Generate filename
      const filename = `property-analysis-${address ? address.replace(/[^a-zA-Z0-9]/g, '-') : 'report'}-${new Date().toISOString().split('T')[0]}.pdf`;
      
      // Save the PDF
      doc.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <button 
      className={`pdf-export-btn ${className}`}
      onClick={generatePDF}
      type="button"
    >
      📄 Export PDF
    </button>
  );
};

export default PDFExportButton;